import { useCallback, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useAuth0 } from '@auth0/auth0-react';
import { useReactToPrint } from 'react-to-print';
import moment from 'moment';
import { toast } from 'react-toastify';

import { ENTITY_PREFIX } from '../../../../../constants';
import { formatEntityId } from '../../../../../helpers';
import { goalActions, sessionActions, treatmentRecommendationActions } from '../../../../../store';
import { PROGRESS_REPORT_DATE_FORMAT, PROGRESS_REPORT_LOOKBACK_DAYS } from '../constants';
import { isMultiTrialSummaryEmpty, toClientOption } from '../utils';
import useTreatmentPlanV3HeaderState from './useTreatmentPlanV3HeaderState';

import type { AppDispatch } from '../../../../../store';
import type { ITreatmentPlanGoal, ITrialSummary } from '../../../../../utils/types';
import type {
  HeaderDialogController,
  ProgressReportDependencies,
  UseProgressReportResult,
} from './types';

export default function useTreatmentPlanV3ProgressReport(
  dialog: HeaderDialogController,
): UseProgressReportResult {
  const dispatch = useDispatch<AppDispatch>();
  const { getAccessTokenSilently } = useAuth0();
  const { treatmentPlan, selectedClient, reportGoals, multiTrialSummary } =
    useTreatmentPlanV3HeaderState();

  return useTreatmentPlanV3ProgressReportWithDependencies({
    dispatch,
    actions: {
      getGoals: goalActions.getGoals,
      getMultiTrialSummary: sessionActions.getMultiTrialSummary,
      getRecommendation: treatmentRecommendationActions.getRecommendation,
    },
    getAccessTokenSilently,
    treatmentPlan,
    clientId: selectedClient?.id,
    clientLabel: selectedClient ? toClientOption(selectedClient).label : '',
    hasReportData: reportGoals.length > 0 && multiTrialSummary.length > 0,
    dialog,
  });
}

export function useTreatmentPlanV3ProgressReportWithDependencies({
  dispatch,
  actions,
  getAccessTokenSilently,
  treatmentPlan,
  clientId,
  clientLabel,
  hasReportData,
  dialog,
}: ProgressReportDependencies): UseProgressReportResult {
  const { openDialog, closeDialog } = dialog;
  const contentRef = useRef<HTMLDivElement>(null);
  const [isReportRunning, setIsReportRunning] = useState(false);
  const treatmentPlanId = treatmentPlan?.id;

  const handlePrint = useReactToPrint({
    documentTitle: `${clientLabel}_${formatEntityId(treatmentPlanId ?? '', ENTITY_PREFIX.TreatmentPlan)}`,
    contentRef,
  });

  const requestReport = useCallback(async () => {
    if (treatmentPlanId === undefined || clientId === undefined) return;
    setIsReportRunning(true);
    openDialog({ kind: 'reportProgress' });
    try {
      const goalsResponse = await dispatch(
        actions.getGoals({ getAccessTokenSilently, treatmentPlanId, tp_report_goals: true }),
      );
      const goals: ITreatmentPlanGoal[] = goalsResponse.payload ?? [];
      const goalIds = goals.map((goal) => goal.id).filter((id): id is number => id !== undefined);
      if (!goalIds.length) {
        toast.error('No goals found for the selected treatment plan');
        closeDialog();
        return;
      }
      await dispatch(actions.getRecommendation({ treatmentPlanId, getAccessTokenSilently }));
      const summaryResponse = await dispatch(
        actions.getMultiTrialSummary({
          params: {
            client_id: clientId,
            treatment_plan_id: treatmentPlanId,
            start_date: moment()
              .subtract(PROGRESS_REPORT_LOOKBACK_DAYS, 'days')
              .format(PROGRESS_REPORT_DATE_FORMAT),
            end_date: moment().format(PROGRESS_REPORT_DATE_FORMAT),
            goal_ids: goalIds,
          },
          getAccessTokenSilently,
        }),
      );
      if (summaryResponse.error) {
        toast.error('Failed to generate report, please try again later');
        closeDialog();
        return;
      }
      const summary: ITrialSummary[][] = summaryResponse.payload ?? [];
      if (isMultiTrialSummaryEmpty(summary)) {
        toast.error('There are no trials available for the selected treatment plan.');
        closeDialog();
        return;
      }
      openDialog({ kind: 'reportConfirm' });
    } catch {
      toast.error('An unexpected error occurred while generating the report');
      closeDialog();
    } finally {
      setIsReportRunning(false);
    }
  }, [
    actions,
    clientId,
    closeDialog,
    dispatch,
    getAccessTokenSilently,
    openDialog,
    treatmentPlanId,
  ]);

  const print = useCallback(() => {
    handlePrint();
    closeDialog();
  }, [closeDialog, handlePrint]);

  return { requestReport, print, contentRef, isReportReady: hasReportData, isReportRunning };
}
